// src/utils/productService.js

/**
 * ============================================================
 * GUIDE — ENDPOINTS BACKEND
 * ============================================================
 *
 * Ici on centralise toutes les URLs backend.
 *
 * Objectif :
 * - éviter les URLs dispersées dans le front
 * - simplifier les changements futurs de nom de fichiers PHP
 * - ajouter les nouveaux endpoints sans casser l’existant
 *
 * Convention projet :
 * - endpoints PHP à plat dans /services/*.php
 */
const API_BASE = "https://marchedeluxe.ch/services";

const ENDPOINTS = {
  classic: {
    create: `${API_BASE}/ajouter_produit.php`,
    update: `${API_BASE}/modifier_produit_general.php`,
    upload: `${API_BASE}/upload_image.php`,
  },

  cave: {
    create: `${API_BASE}/ajouter_produit_cave.php`,
    update: `${API_BASE}/modifier_produit_cave.php`,

    /**
     * Si pas encore d’upload cave dédié,
     * on recycle l’uploader classique.
     */
    upload: `${API_BASE}/upload_image.php`,
  },

  /**
   * ==========================================================
   * NOUVEAU — MILK TYPE SYSTEM
   * ==========================================================
   *
   * Endpoints dédiés au système "type de lait"
   * sans toucher aux endpoints produits existants.
   */
  milk: {
    adminList: `${API_BASE}/get_produits_lait_admin.php`,
    save: `${API_BASE}/sauvegarder_produit_lait.php`,
    catalogue: `${API_BASE}/get_catalogue_avec_lait.php`,
  },
};

/**
 * ============================================================
 * GUIDE — HEADERS AUTH
 * ============================================================
 *
 * Tous les appels passent ici pour :
 * - réutiliser le token admin
 * - garder une logique uniforme
 * - éviter les divergences fetch() entre modules
 *
 * @param {boolean} isJson
 * @returns {Object}
 */
const authHeaders = (isJson = true) => {
  const token = localStorage.getItem("user_token") || "";
  const h = {};

  if (isJson) h["Content-Type"] = "application/json";
  if (token) h["Authorization"] = `Bearer ${token}`;

  return h;
};

/**
 * ============================================================
 * GUIDE — HELPERS SCOPE
 * ============================================================
 *
 * Le scope permet de distinguer :
 * - "classic"
 * - "cave"
 *
 * ⚠️ Important :
 * Le système lait n’est PAS un scope produit.
 * C’est un enrichissement séparé.
 */
const normalizeScope = (scope) => (scope === "cave" ? "cave" : "classic");

const resolveScopeFromPayload = (payload = {}) => {
  if (payload?.__productScope === "cave") return "cave";
  if (payload?.product_scope === "cave") return "cave";
  if (payload?.__isCave === true) return "cave";
  if (Number(payload?.isCave || 0) === 1) return "cave";
  return "classic";
};

/**
 * ============================================================
 * GUIDE — NORMALISATION PAYLOAD PRODUIT
 * ============================================================
 *
 * Transforme le formulaire React en payload backend propre :
 * - prix => centimes
 * - booléens => 0 / 1
 * - strings => trim
 *
 * ⚠️ Cette fonction ne gère PAS le type_lait.
 * Le type_lait passe dans une logique backend séparée.
 */
const buildProductBody = (payload = {}, scope = "classic") => {
  const finalScope = normalizeScope(scope);

  return {
    name: String(payload?.name || "").trim(),
    category: payload?.category || "",
    histoire: payload?.histoire || "",
    unite: payload?.unite || "KG",

    // centimes (int)
    price: Number.isFinite(Number(payload?.price))
      ? Math.round(Number(payload.price))
      : 0,
    old_price: Number.isFinite(Number(payload?.old_price))
      ? Math.round(Number(payload.old_price))
      : 0,
    pricePro: Number.isFinite(Number(payload?.pricePro))
      ? Math.round(Number(payload.pricePro))
      : 0,

    // états (int 0/1)
    isOnline: Number(payload?.isOnline) === 1 ? 1 : 0,
    isDiscount: Number(payload?.isDiscount) === 1 ? 1 : 0,

    // stock (int)
    stock: Number.isFinite(Number(payload?.stock))
      ? Math.round(Number(payload.stock))
      : 0,
    // champs classiques / épicerie
    origine: String(payload?.origine || "").trim(),
    texture: String(payload?.texture || "").trim(),
    intensite: String(payload?.intensite || "").trim(),

    // champs cave
    millesime: String(payload?.millesime || "").trim(),
    region: String(payload?.region || "").trim(),
    cepage: String(payload?.cepage || "").trim(),

    // métadonnées de scope
    product_scope: finalScope,
    isCave: finalScope === "cave" ? 1 : 0,
  };
};

/**
 * ============================================================
 * GUIDE — LECTURE STANDARD DES RÉPONSES
 * ============================================================
 *
 * Tous les backends ne renvoient pas exactement les mêmes clés.
 * On homogénéise ici :
 * - success
 * - id
 * - product_id
 * - message
 * - error
 */
const readJsonSafe = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

const extractCreatedId = (data) => {
  return data?.id || data?.product_id || data?.insert_id || null;
};

/**
 * ============================================================
 * GUIDE — NORMALISATION TYPE DE LAIT
 * ============================================================
 *
 * Valeurs autorisées côté front pour rester aligné
 * avec l’ENUM SQL / validation PHP.
 *
 * ⚠️ On tolère la chaîne vide pour permettre
 * la suppression de l’enrichissement.
 */
const ALLOWED_MILK_TYPES = ["", "lait cru", "pasteurisé", "thermisé"];

const normalizeMilkType = (value) => {
  const v = typeof value === "string" ? value.trim() : "";
  return ALLOWED_MILK_TYPES.includes(v) ? v : "";
};

/**
 * ============================================================
 * GUIDE — AJOUT PRODUIT
 * ============================================================
 *
 * Utilisation normale :
 *   ajouterProduit(payload)
 *
 * Si payload contient :
 *   __productScope: "cave"
 * ou product_scope: "cave"
 *
 * alors l’ajout part automatiquement vers le backend cave.
 */
export async function ajouterProduit(payload) {
  const scope = resolveScopeFromPayload(payload);
  const body = buildProductBody(payload, scope);

  if (!body.name) {
    throw new Error("Nom du produit manquant.");
  }

  const endpoint = ENDPOINTS[scope].create;

  const res = await fetch(endpoint, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });

  const data = await readJsonSafe(res);

  if (!res.ok || !data?.success) {
    const msg =
      data?.message ||
      data?.error ||
      `Ajout produit ${scope === "cave" ? "cave" : ""} impossible.`;
    throw new Error(msg);
  }

  return extractCreatedId(data);
}

/**
 * ============================================================
 * GUIDE — UPLOAD IMAGE
 * ============================================================
 *
 * Utilisation :
 *   uploaderImage(productId, file)
 *   uploaderImage(productId, file, "cave")
 *
 * Si tu ne fournis pas le scope :
 * - classic par défaut
 */
export async function uploaderImage(productId, file, scope = "classic") {
  if (!productId || !file) return { success: false };

  const finalScope = normalizeScope(scope);
  const token = localStorage.getItem("user_token") || "";
  const formData = new FormData();

  /**
   * Le PHP actuel attend :
   * - image
   * - id
   */
  formData.append("image", file);
  formData.append("id", String(productId));
  formData.append("product_scope", finalScope);

  const res = await fetch(ENDPOINTS[finalScope].upload, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  });

  const data = await readJsonSafe(res);

  if (!res.ok || !data?.success) {
    return {
      success: false,
      error: data?.message || data?.error || "Upload failed",
    };
  }

  return {
    success: true,
    image: data.image,
  };
}

/**
 * ============================================================
 * GUIDE — UPDATE COMPLET PRODUIT
 * ============================================================
 *
 * Utilisation :
 *   mettreAJourProduitComplet(formP)
 *
 * Le scope est détecté automatiquement depuis formP :
 * - __productScope
 * - product_scope
 * - __isCave
 * - isCave
 *
 * ⚠️ Important :
 * Cette fonction ne sauvegarde PAS le type_lait.
 * Le type_lait reste dans son endpoint dédié pour :
 * - éviter tout couplage risqué
 * - préserver les modals existantes
 * - garder un rollback simple en prod
 */
export async function mettreAJourProduitComplet(formP) {
  try {
    const scope = resolveScopeFromPayload(formP);

    const body = {
      ...buildProductBody(formP, scope),
      complet: true,
      id: formP?.id,
    };

    const res = await fetch(ENDPOINTS[scope].update, {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify(body),
    });

    const data = await readJsonSafe(res);

    return !!(res.ok && data?.success);
  } catch (err) {
    console.error(err);
    return false;
  }
}

/**
 * ============================================================
 * GUIDE — CATALOGUE ENRICHI TYPE DE LAIT
 * ============================================================
 *
 * Lecture front du catalogue avec LEFT JOIN type_lait.
 *
 * Usage typique :
 *   const products = await recupererCatalogueAvecLait();
 *
 * Retour :
 * - tableau de produits bruts backend
 * - chaque produit peut contenir type_lait (ou non)
 *
 * ⚠️ Cette fonction est volontairement en lecture seule.
 * Elle ne remplace pas les endpoints admin existants ;
 * elle sert à enrichir l’affichage boutique.
 */
export async function recupererCatalogueAvecLait() {
  const res = await fetch(ENDPOINTS.milk.catalogue, {
    method: "GET",
    headers: authHeaders(false),
  });

  const data = await readJsonSafe(res);

  if (!res.ok || !data?.success) {
    throw new Error(
      data?.message ||
        data?.error ||
        "Chargement du catalogue enrichi impossible."
    );
  }

  return Array.isArray(data?.products) ? data.products : [];
}

/**
 * ============================================================
 * GUIDE — ADMIN LISTE PRODUITS + TYPE DE LAIT
 * ============================================================
 *
 * Lecture dédiée à la page admin type de lait.
 *
 * Usage :
 *   const items = await recupererProduitsLaitAdmin();
 *
 * Retour :
 * - liste des produits
 * - enrichissement type_lait si présent
 */
export async function recupererProduitsLaitAdmin() {
  const res = await fetch(ENDPOINTS.milk.adminList, {
    method: "GET",
    headers: authHeaders(false),
  });

  const data = await readJsonSafe(res);

  if (!res.ok || !data?.success) {
    throw new Error(
      data?.message ||
        data?.error ||
        "Chargement admin type de lait impossible."
    );
  }

  return Array.isArray(data?.items) ? data.items : [];
}

/**
 * ============================================================
 * GUIDE — SAUVEGARDE TYPE DE LAIT
 * ============================================================
 *
 * Sauvegarde unitaire du type de lait d’un produit.
 *
 * Usage :
 *   await sauvegarderTypeLait(12, "lait cru")
 *   await sauvegarderTypeLait(12, "")
 *
 * Cas particuliers :
 * - "" => suppression de l’enrichissement côté backend
 * - valeur inconnue => normalisée en ""
 *
 * Retour :
 * - payload backend complet si succès
 */
export async function sauvegarderTypeLait(produitId, typeLait, laitAnimal = "") {
  const finalProductId = Number(produitId);
  const finalTypeLait = normalizeMilkType(typeLait);
  const finalLaitAnimal = String(laitAnimal || "").trim().toLowerCase();

  if (!Number.isFinite(finalProductId) || finalProductId <= 0) {
    throw new Error("produit_id invalide.");
  }

  const res = await fetch(ENDPOINTS.milk.save, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify({
      produit_id: finalProductId,
      type_lait: finalTypeLait,
      lait_animal: finalLaitAnimal,
    }),
  });

  const data = await readJsonSafe(res);

  if (!res.ok || !data?.success) {
    throw new Error(
      data?.message || data?.error || "Sauvegarde du type de lait impossible."
    );
  }

  return data;
}

/**
 * ============================================================
 * GUIDE — EXPORT TECHNIQUE OPTIONNEL
 * ============================================================
 *
 * Peut être utile pour debug / tests / maintenance.
 * Aucun impact si non utilisé.
 */
export const PRODUCT_SERVICE_ENDPOINTS = ENDPOINTS;
export const PRODUCT_SERVICE_ALLOWED_MILK_TYPES = ALLOWED_MILK_TYPES;
